import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Bot, Mic, MicOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea, type TextareaProps } from "@/components/ui/textarea";
import { useVoiceRecognition } from "@/hooks/useVoiceRecognition";
import { useMartinaGlobal } from "@/contexts/ExAiGlobalContext";
import { appendItalianDictation } from "@/lib/italianDictation";
import type { MartinaTerminalContext } from "@/components/exai/ExAiGlobalDrawer";
import { MartinaComposerInlineField } from "@/components/exai/MartinaComposerInlineField";
import { fetchComposerMentionHits } from "@/components/exai/fetchComposerMentionHits";
import {
  activeComposerAtQuery,
  activeComposerPlainNameQuery,
  applyUniqueMentionToSegments,
  composerSegmentsFromPlainText,
  displayComposerText,
  reconcileComposerSegmentsFromDisplay,
  resolveComposerMentionUniqueness,
  serializeComposerPayload,
  uniquenessFromSelectedComposerHit,
  type MartinaComposerCrmHit,
  type MartinaComposerSegment,
} from "@/components/exai/martinaComposerMentionContract";
import { cn } from "@/lib/utils";

type VoiceAssistantTextareaProps = Omit<TextareaProps, "value" | "onChange"> & {
  value: string;
  onValueChange: (value: string) => void;
  fieldLabel: string;
  martinaContext?: MartinaTerminalContext;
  /** Social-style inline `@Nome` picker for CRM notes and chat-like fields. */
  mentions?: boolean;
  /** Hide dictation/Martina action buttons when the field is a compact sub-note. */
  showActions?: boolean;
  /** Start dictation once when a dedicated voice action opens this field. */
  autoStartVoice?: boolean;
  /** Optional transport payload with canonical CRM refs for a caller that needs it. */
  onSerializedValueChange?: (value: string) => void;
};

export function VoiceAssistantTextarea({
  value,
  onValueChange,
  fieldLabel,
  martinaContext,
  mentions = false,
  showActions = true,
  autoStartVoice = false,
  onSerializedValueChange,
  disabled,
  className,
  ...props
}: VoiceAssistantTextareaProps) {
  const { openMartina } = useMartinaGlobal();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const baseValueRef = useRef(value);
  const ignoreLateTranscriptRef = useRef(false);
  const baseSegmentsRef = useRef<MartinaComposerSegment[]>(composerSegmentsFromPlainText(value));
  const segmentsRef = useRef<MartinaComposerSegment[]>(baseSegmentsRef.current);
  const [segments, setSegments] = useState<MartinaComposerSegment[]>(baseSegmentsRef.current);
  const [mentionChoices, setMentionChoices] = useState<MartinaComposerCrmHit[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [composerCaret, setComposerCaret] = useState(0);
  const mentionGateRef = useRef(0);

  useEffect(() => {
    if (!mentions) return;
    const visible = displayComposerText(segmentsRef.current);
    if (value === visible) return;
    const next = composerSegmentsFromPlainText(value);
    segmentsRef.current = next;
    setSegments(next);
    setMentionChoices([]);
    setMentionQuery(null);
  }, [mentions, value]);

  useEffect(() => {
    if (!mentions) return;
    const at = activeComposerAtQuery(segments, composerCaret);
    const plainName = at ? null : activeComposerPlainNameQuery(segments, composerCaret);
    const query = at && at.query.trim().length >= 2
      ? at.query.trim()
      : plainName && plainName.text.trim().length >= 3
        ? plainName.text.trim()
        : null;
    const gate = mentionGateRef.current + 1;
    mentionGateRef.current = gate;
    setMentionChoices([]);
    setMentionQuery(query);
    if (!query) return;
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const hits = await fetchComposerMentionHits(query);
          const uniqueness = resolveComposerMentionUniqueness(query, hits);
          if (gate !== mentionGateRef.current) return;
          // Anche il match unico richiede una scelta esplicita: il testo
          // digitato non può collegarsi da solo a un omonimo.
          setMentionChoices(uniqueness.status === 'ambiguous' ? uniqueness.hits : hits.slice(0, 12));
        } catch {
          if (gate === mentionGateRef.current) {
            setMentionChoices([]);
            setMentionQuery(null);
          }
        }
      })();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [mentions, segments, composerCaret, onSerializedValueChange, onValueChange]);

  const transcriptHandler = useCallback((finalText: string, interimText: string) => {
    // A manual correction owns the field from that point onward. Web Speech
    // may still deliver a queued result after stop(); never replay the old base.
    if (ignoreLateTranscriptRef.current) return;
    const appended = appendItalianDictation(finalText, interimText);
    if (!mentions) {
      onValueChange(appendItalianDictation(baseValueRef.current, appended));
      return;
    }
    const base = displayComposerText(baseSegmentsRef.current);
    const nextDisplay = appendItalianDictation(base, appended);
    const known = baseSegmentsRef.current.filter((segment): segment is Extract<MartinaComposerSegment, { kind: 'chip' }> => segment.kind === 'chip');
    // Dictation is appended after the last stable token; preserve prior CRM refs.
    const reconciled = reconcileComposerSegmentsFromDisplay(known, nextDisplay);
    segmentsRef.current = reconciled;
    setSegments(reconciled);
    onValueChange(serializeComposerPayload(reconciled));
    onSerializedValueChange?.(serializeComposerPayload(reconciled));
  }, [mentions, onSerializedValueChange, onValueChange]);
  const prepareVoiceSession = useCallback(() => {
    baseValueRef.current = mentions ? displayComposerText(segmentsRef.current) : value;
    baseSegmentsRef.current = mentions ? segmentsRef.current : composerSegmentsFromPlainText(value);
    ignoreLateTranscriptRef.current = false;
  }, [mentions, value]);
  const { isSupported, isListening, error, start, stop, cancel } = useVoiceRecognition(
    transcriptHandler,
    undefined,
    4_500,
    { focusTargetRef: textareaRef, onSessionStart: prepareVoiceSession },
  );
  useLayoutEffect(() => {
    if (!disabled) return;
    ignoreLateTranscriptRef.current = true;
    cancel();
    mentionGateRef.current++;
    setMentionChoices([]);
  }, [disabled, cancel]);
  const stopForManualEdit = useCallback(() => {
    ignoreLateTranscriptRef.current = true;
    stop();
  }, [stop]);
  const updateSegments = useCallback((next: MartinaComposerSegment[]) => {
    if (disabled) return;
    stopForManualEdit();
    segmentsRef.current = next;
    setSegments(next);
    onValueChange(serializeComposerPayload(next));
    onSerializedValueChange?.(serializeComposerPayload(next));
    setMentionChoices([]);
    setMentionQuery(null);
  }, [disabled, onSerializedValueChange, onValueChange, stopForManualEdit]);
  const toggleVoice = () => {
    if (disabled) return;
    if (isListening) {
      stop();
      return;
    }
    start();
  };

  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!autoStartVoice || !isSupported || disabled || autoStartedRef.current) return;
    // React StrictMode replays mount effects in development. Deferring the
    // attempt lets the replay cancel its first timer without consuming the
    // one-shot action or aborting a pending microphone permission request.
    const timer = window.setTimeout(() => {
      if (autoStartedRef.current) return;
      autoStartedRef.current = true;
      start();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [autoStartVoice, disabled, isSupported, mentions, start, value]);

  const visibleValue = mentions ? displayComposerText(segments) : value;
  const martinaPrompt = useMemo(() => [
    `Sto compilando il campo “${fieldLabel}”.`,
    visibleValue.trim() ? `Testo attuale:\n${visibleValue.trim()}` : "Il campo e' ancora vuoto.",
    "Aiutami a ragionare o a preparare una proposta. Non modificare o salvare il campo automaticamente.",
  ].join("\n\n"), [fieldLabel, visibleValue]);

  return (
    <div className="space-y-2">
      {mentions ? (
        <>
          <MartinaComposerInlineField
            segments={segments}
            onSegmentsChange={updateSegments}
            onCaretChange={setComposerCaret}
            readOnly={disabled}
            id={props.id}
            placeholder={props.placeholder}
            ariaLabel={props['aria-label']}
            rows={props.rows}
            textareaClassName={cn(className, disabled && 'cursor-not-allowed opacity-50')}
            onKeyDown={props.onKeyDown}
            ref={textareaRef}
          />
          {mentionChoices.length > 0 && (
            <ul role="listbox" aria-label={`Scegli il record CRM per ${mentionQuery ?? 'la menzione'}`} className="max-h-40 overflow-y-auto rounded-md border border-border bg-background">
              {mentionChoices.map((hit) => (
                <li key={`${hit.type}:${hit.id}`}>
                  <button
                    type="button"
                    disabled={disabled}
                    className="flex min-h-11 w-full items-center px-3 py-2 text-left text-sm hover:bg-muted"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      const uniqueness = uniquenessFromSelectedComposerHit(hit);
                      if (!uniqueness) return;
                      const at = activeComposerAtQuery(segmentsRef.current, composerCaret);
                      const next = applyUniqueMentionToSegments(segmentsRef.current, at?.query.trim() || hit.title, uniqueness, composerCaret);
                      updateSegments(next);
                    }}
                  >
                    <span className="font-medium">{hit.title}</span>
                    <span className="ml-2 text-xs font-medium uppercase tracking-wide text-primary/80">{hit.type}</span>
                    {hit.subtitle ? <span className="ml-2 text-xs italic text-muted-foreground">{hit.subtitle}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <Textarea
          {...props}
          ref={textareaRef}
          disabled={disabled}
          value={value}
          className={className}
          onChange={(event) => {
            stopForManualEdit();
            onValueChange(event.target.value);
          }}
        />
      )}
      {showActions && <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled || !isSupported}
          onClick={toggleVoice}
          aria-pressed={isListening}
        >
          {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          {isListening ? "Ferma dettatura" : "Detta"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => openMartina({ context: martinaContext, prompt: martinaPrompt })}
        >
          <Bot className="h-4 w-4" />
          Chiedi a Martina
        </Button>
        {error && <p className="text-xs text-destructive">{error}</p>}
        {autoStartVoice && !isSupported && <p className="text-xs text-muted-foreground">Questo browser non supporta la dettatura. Puoi scrivere la nota qui.</p>}
      </div>}
    </div>
  );
}
